import client from './client';
import type { GeoJSONLineString, GeoJSONPolygon } from './geo';

/**
 * Геометрия уведомления — линия перекрытия или замкнутая зона (Заход 4,
 * 04.09.2026, колонка `city_notices.geom`). ⚠️ null — норма, а не пропуск: у
 * актированного дня зоны нет вовсе, он касается всего города.
 */
export type NoticeGeom = GeoJSONLineString | GeoJSONPolygon | null;

/**
 * Городские уведомления — то, что попадает ПЕРВЫМ в блок «Сегодня в городе»
 * на Главной приложения: перекрытая дорога, актированный день, ограничение.
 * Заведено 18.08.2026 по замыслу владельца; у этих сообщений не было своего
 * места вовсе (в «Отключения» их класть нельзя — там только ЖКХ).
 */
export type NoticeKind =
  | 'road_closed'
  | 'school_closed'
  | 'transport'
  | 'emergency'
  | 'quarantine'
  | 'water_supply'
  | 'other';

export type NoticeSeverity = 'critical' | 'warning' | 'info';

export const NOTICE_KIND_LABELS: Record<NoticeKind, string> = {
  road_closed: 'Дорога перекрыта',
  school_closed: 'Актированный день (школы)',
  transport: 'Транспорт',
  emergency: 'ЧС / предупреждение',
  quarantine: 'Карантин, ограничения',
  water_supply: 'Подвоз воды',
  other: 'Другое',
};

export const NOTICE_SEVERITY_LABELS: Record<NoticeSeverity, string> = {
  critical: 'Критично',
  warning: 'Предупреждение',
  info: 'Информация',
};

export interface CityNotice {
  id: number;
  kind: NoticeKind;
  severity: NoticeSeverity;
  title_ru: string;
  title_kk?: string | null;
  body_ru?: string | null;
  body_kk?: string | null;
  affected_addresses?: string[];
  district_id?: number | null;
  starts_at: string;
  ends_at?: string | null;
  is_active: boolean;
  created_at: string;
  geom?: NoticeGeom;
}

export interface CityNoticeListResponse {
  items: CityNotice[];
  total: number;
  page: number;
  size: number;
}

export interface CityNoticePayload {
  kind: NoticeKind;
  severity: NoticeSeverity;
  title_ru: string;
  title_kk?: string;
  body_ru?: string;
  body_kk?: string;
  affected_addresses?: string[];
  district_id?: number | null;
  starts_at: string;
  /** Пусто = «до отмены»: уведомление держится, пока его не снимут. */
  ends_at?: string | null;
  /**
   * ⚠️ `null` ЗДЕСЬ ЗНАЧИТ «СТЕРЕТЬ», а не «не трогать»: сервер разбирает тело
   * через `exclude_unset`, поэтому не отправленное поле остаётся как было.
   * Отправляем его всегда — форма рисования и есть источник правды о зоне.
   */
  geom?: NoticeGeom;
}

export const cityNoticesApi = {
  list: (params?: { include_inactive?: boolean; page?: number; size?: number }) =>
    client.get<CityNoticeListResponse>('/api/admin/city-notices', { params }),

  create: (data: CityNoticePayload) =>
    client.post<CityNotice>('/api/admin/city-notices', data),

  update: (id: number, data: Partial<CityNoticePayload> & { is_active?: boolean }) =>
    client.patch<CityNotice>(`/api/admin/city-notices/${id}`, data),

  /** Снимает с показа (строка остаётся в базе — это городская история). */
  deactivate: (id: number) =>
    client.delete<{ ok: boolean }>(`/api/admin/city-notices/${id}`),
};
